/**
 * PATH: packages/bills/src/types.ts
 * FILE_ID: BILLS-PKG-TYPES-001
 * PURPOSE: Single source of truth for the Bills feature's TypeScript types.
 *
 * WHY: Both `apps/sam/src/features/bills/hooks/useBills.ts` and
 *      `apps/billing/src/hooks/useBills.ts` had drifted definitions of
 *      `BillListRow` / `BillDetail` / `BillStats`. Drift caused the
 *      Phase A regression where the wrong Xero-push URL shipped — there
 *      was no shared contract to pin against. Centralising the types
 *      here means a backend serializer change is one edit, not two.
 *
 * FLOW:
 *   organisation/views/bill_views/serializers.py (backend)
 *     ─┬─→ packages/bills/src/types.ts  (this file — single contract)
 *      ├─→ apps/sam/src/features/bills/hooks/useBills.ts (consumes)
 *      └─→ apps/billing/src/hooks/useBills.ts            (consumes)
 *
 * DEPENDENCIES: none (pure type module — keep it that way).
 *
 * RELATED:
 *   - repos/quotech-backend/organisation/views/bill_views/serializers.py
 *   - packages/bills/src/endpoints.ts
 */

export type BillStatus =
  | 'pending'
  | 'extracted'
  | 'reviewed'
  | 'pushed'
  | 'failed'
  // Leak-proof verification cascade fail-closed states (BE-MR1..4).
  // Mirrors ``ExtractedBill.STATUS_*`` on the backend. Hidden from the
  // default Bills list; surfaced via their own review tabs (``?status=``)
  // and reversible via POST /api/bills/<id>/restore/.
  | 'quarantined'
  | 'awaiting_document'
  | 'needs_fetch'
  | 'needs_review';

/**
 * The cascade fail-closed subset of ``BillStatus`` — the review queues the
 * bookkeeper triages out of band. Single source of truth so the list tabs,
 * the restore affordance, and the status pills agree on which statuses are
 * "held back" rather than part of the normal pending→pushed pipeline.
 */
export const FAIL_CLOSED_STATUSES: readonly BillStatus[] = [
  'quarantined',
  'awaiting_document',
  'needs_fetch',
  'needs_review',
];

export function isFailClosedStatus(status: BillStatus): boolean {
  return (FAIL_CLOSED_STATUSES as readonly string[]).includes(status);
}

/**
 * Query-side status group accepted by ``GET /api/bills/?status=blocked``:
 * failed + the four fail-closed cascade states — every state where the
 * machine tried and could not produce a reviewable bill. Exists so the
 * queue's pipeline chips are each a REAL filter whose counts partition
 * ``counts.total`` exactly (Phase-2 requirement of the queue redesign;
 * see docs/plans/bills-queue-redesign/README.md).
 */
export type BillStatusFilter = BillStatus | 'all' | 'blocked';

export const BLOCKED_STATUSES: readonly BillStatus[] = [
  'failed',
  ...FAIL_CLOSED_STATUSES,
];

/**
 * Attention-reason filters (``?flag=``) — row-data predicates the flat
 * status enum cannot express. Mirrors ``_apply_flag_filter`` on the
 * backend (organisation/views/bill_views/list_detail_views.py):
 *   - ``unreadable``  extraction failed, untouched by a human
 *   - ``rejected``    failed because a person rejected it
 *   - ``no_amount``   review-stage bill with no trustworthy total
 *   - ``duplicate``   shares (vendor, invoice#) with another bill
 */
export type BillFlag = 'unreadable' | 'rejected' | 'no_amount' | 'duplicate';

/** List ordering: server default is newest-received; ``triage`` is the
 * operator queue order (overdue first, then due-soonest, then largest
 * known amount; unknowns fall back to oldest-received). */
export type BillListOrder = 'received' | 'triage';

/**
 * Paid state of a bill — INDEPENDENT of the extraction ``status`` above.
 * Mirrors ``ExtractedBill.PAYMENT_*`` on the backend
 * (organisation/models/bills.py). A bill can be ``reviewed``/``pushed`` AND
 * ``paid`` at the same time; the two enums never collapse into one.
 */
export type BillPaymentStatus = 'unpaid' | 'paid' | 'partially_paid';

/**
 * The operator's internal supplier payment-term buckets. Mirrors
 * ``quotebuilder.payment_terms.SupplierTermType`` on the backend. The term
 * lives on the SUPPLIER (so every bill for that supplier inherits it) and
 * drives the bill's predicted due date. ``custom`` pairs with a day count
 * (``payment_term_days``); every other bucket has a fixed offset.
 */
export type SupplierTermType =
  | 'cod'
  | 'eod'
  | 'net_7'
  | 'net_30'
  | 'net_45'
  | 'net_60'
  | 'net_90'
  | 'eom'
  | 'custom';

/**
 * A term recorded on a single BILL — every supplier bucket, plus
 * ``exempt``. A bill is normally blank, meaning "inherit the supplier";
 * ``exempt`` says this one deliberately follows NO term, which blank
 * cannot express. Suppliers can never be exempt.
 */
export type BillTermType = SupplierTermType | 'exempt';

/**
 * Which level decided the term governing a bill:
 *   ``bill``     — a one-off exception recorded on this bill;
 *   ``supplier`` — inherited from the vendor (the normal case);
 *   ``exempt``   — deliberately no term;
 *   ``none``     — nobody has set one yet.
 */
export type BillTermSource = 'bill' | 'supplier' | 'exempt' | 'none';

/**
 * How the bill came into Quotech. Mirrors ``ExtractedBill.SOURCE_*``
 * on the backend (organisation/models/bills.py). The default is
 * ``email`` — the canonical AP pipeline (mailbox sync → category ==
 * 'bill' → auto-materialise → Deepseek extract). ``manual`` rows
 * are operator-entered for tenants without a connected mailbox or
 * without Xero, and have ``email_id === null``,
 * ``status === 'reviewed'`` from day one, and skip the AI
 * extraction phase entirely.
 */
export type BillSource = 'email' | 'manual';

/**
 * Evidence-based job-allocation state. Mirrors
 * ``ExtractedBill.ALLOCATION_STATUS_CHOICES`` on the backend
 * (organisation/models/bills.py). The pipeline auto-links a bill to a
 * job ONLY on a verified >=0.95 match (PO or exact job number) to
 * exactly one ``report.Job`` (``linked``); everything weaker is held
 * as a ``suggested`` candidate or routed to ``needs_review`` so a human
 * confirms it. ``unallocated`` is the default before the allocator has
 * run / found anything.
 */
export type BillAllocationStatus =
  | 'unallocated'
  | 'suggested'
  | 'linked'
  | 'needs_review';

/**
 * Which signal drove the allocation. ``po`` / ``job_number`` are the
 * only two trusted enough to auto-link; ``fuzzy`` / ``ai`` /
 * ``sender_history`` are suggestion-only and always land in review;
 * ``manual`` is a human confirmation. Mirrors
 * ``ExtractedBill.ALLOCATION_SOURCE_CHOICES``. Empty string when the
 * allocator has not run.
 */
export type BillAllocationSource =
  | ''
  | 'po'
  | 'job_number'
  | 'fuzzy'
  | 'ai'
  | 'manual'
  | 'sender_history';

/**
 * One typed finding in the bill's ``allocation_evidence`` bundle — the
 * audit trail of the deterministic-first allocation pipeline. Each
 * micro-step (PO lookup, job-number match, fuzzy match, …) emits a
 * finding with its own confidence + provenance so a reviewer can see
 * exactly WHY a bill was linked or held. The shape is intentionally
 * permissive (the backend evolves the bundle additively); the known
 * keys below are what the review surface renders.
 */
export interface BillAllocationFinding {
  step?: string;
  source?: BillAllocationSource | string;
  confidence?: number | string | null;
  job_id?: number | null;
  job_number?: string | null;
  po_number?: string | null;
  detail?: string;
  matched?: boolean;
  [key: string]: unknown;
}

/**
 * Document type as classified by the AI prompt during extraction.
 * Mirrors ``ExtractedBill.DOC_TYPE_CHOICES`` on the backend (see
 * ``organisation/models/bills.py``). ``unknown`` is the safe default
 * for legacy rows extracted before the field existed.
 */
export type BillDocumentType =
  | 'unknown'
  | 'tax_invoice'
  | 'statement'
  | 'remittance'
  | 'credit_note';

export interface BillLineItem {
  description?: string;
  quantity?: number | null;
  unit_amount?: number | null;
  line_total?: number | null;
  tax_type?: string | null;
  account_code?: string | null;
  /**
   * Per-line allocation coding (p3b). All optional + additive — they
   * persist straight into the ``line_items`` JSON via the bill PATCH (no
   * migration). ``account_code`` above is the GL account (CoA leaf);
   * these refine it:
   *   - ``sub_account_code`` — finer breakdown a tenant tracks below the
   *     Xero GL code (free text / secondary code).
   *   - ``cost_category``    — Quotech cost bucket (materials / labour …),
   *     mirrors ``report.XeroAccount.cost_category``.
   *   - ``job_code``         — job/cost-centre this line is charged to.
   * The extractor also emits ``category`` (the bucket vocabulary the Xero
   * push cascade reads) — kept distinct from ``cost_category`` which is the
   * operator's coding choice.
   */
  sub_account_code?: string | null;
  cost_category?: string | null;
  job_code?: string | null;
  [key: string]: unknown;
}

/**
 * One Chart-of-Accounts row, as returned by
 * ``GET /api/cfo/chart-of-accounts/`` (report.views.cfo_chart_of_accounts).
 * Used by the per-line allocation grid's CoA picker. Only the fields the
 * picker needs are typed; the endpoint returns more.
 */
export interface CoaAccount {
  id: number;
  xero_account_id: string;
  code: string | null;
  name: string;
  account_type: string;
  account_type_display?: string;
  tax_type?: string | null;
  reporting_code_name?: string | null;
  status?: string;
}

/**
 * A node in the DERIVED CoA hierarchy (class → group → account) returned
 * under ``tree`` when the endpoint is called with ``?tree=1``. Xero's CoA
 * is flat; the tree is built from account_type + reporting code on the
 * backend (report/services/coa_tree.py). ``node_type`` discriminates:
 * ``class`` / ``group`` carry ``children``; ``account`` spreads a
 * ``CoaAccount``'s fields.
 */
export interface CoaTreeNode {
  node_type: 'class' | 'group' | 'account';
  key?: string;
  label?: string;
  account_count?: number;
  children?: CoaTreeNode[];
  // account-node fields (present when node_type === 'account')
  code?: string | null;
  name?: string;
  account_type?: string;
}

export interface ChartOfAccountsResponse {
  accounts: CoaAccount[];
  count: number;
  tree?: CoaTreeNode[];
}

/**
 * One extractor-emitted bounding box anchoring a field/line-cell to a
 * region of the source PDF. ``field`` is the same key vocabulary the
 * read/edit panes use (``billFieldKeys.ts`` — e.g. ``vendor_name`` or
 * ``line.0.description``). ``page`` is 0-based; ``bbox`` is
 * ``[x0, y0, x1, y1]`` normalised to 0..1 of the page so it survives any
 * render scale. Empty array until the extractor emits coordinates (1d) —
 * the cross-highlight (1c) is the always-on fallback. Mirrors
 * ``BillDetailSerializer.get_field_regions`` on the backend.
 */
export interface BillFieldRegion {
  field: string;
  page: number;
  bbox: [number, number, number, number];
}

export interface BillListRow {
  id: string;
  /**
   * Provenance discriminator. ``'email'`` for AI-extracted rows from
   * the mailbox sync pipeline; ``'manual'`` for operator-entered rows.
   * Manual rows have ``email_*`` fields null. Added 2026-05-17.
   */
  source: BillSource;
  status: BillStatus;
  /**
   * Evidence-based job-allocation state (verified-only auto-link). See
   * ``BillAllocationStatus``. The list view uses this to flag rows that
   * need a human to confirm/correct a job link (``needs_review``) vs.
   * those already linked on a verified match.
   */
  allocation_status: BillAllocationStatus;
  /** 0.000–1.000 confidence in the job link, or null before the
   * allocator runs. DRF emits the DecimalField as a string. */
  allocation_confidence: string | null;
  /** Which signal drove the allocation (``po`` / ``job_number`` auto-
   * link; everything else is suggestion-only). */
  allocation_source: BillAllocationSource;
  /** Canonical ``report.Job`` id this bill is allocated to, or null. */
  linked_job_id: number | null;
  /** Display name of the linked job (client name, falling back to
   * ``Job <id>``); null when unlinked. */
  linked_job_name: string | null;
  vendor_name: string;
  vendor_email: string;
  vendor_abn: string;
  invoice_number: string;
  reference: string;
  invoice_date: string | null;
  due_date: string | null;
  subtotal: string | null;
  gst: string | null;
  total: string | null;
  currency: string;
  /**
   * Paid state for the list "Paid" pill. Mirrors ``payment_status`` /
   * ``paid_at`` on the detail; the list view renders these alongside the
   * extraction-status + overdue badges. Defaults to ``'unpaid'`` / null.
   */
  payment_status: BillPaymentStatus;
  paid_at: string | null;
  extraction_confidence: string | null;
  /**
   * Failure provenance for the triage queue — the row can say WHY it is
   * blocked without a detail fetch. ``extraction_error`` is the
   * human-readable extractor message; ``verification_reason`` is the
   * cascade's machine-readable code (``no_total`` / ``totals_mismatch`` /
   * ``self_issued`` / ``verifier_rejected`` / ``low_confidence`` / …).
   * Optional until the backend MR serializing them is deployed.
   */
  extraction_error?: string;
  verification_reason?: string;
  /**
   * Human-touch provenance. ``reviewed_at``/``reviewed_by_name`` are set
   * by approve/edit; ``corrections_count`` > 0 means a person corrected
   * AI-read fields (BillExtractionCorrection rows exist) — such rows are
   * excluded from bulk re-reads by default because re-extraction would
   * overwrite the human's work. Optional until the backend MR lands.
   */
  reviewed_at?: string | null;
  reviewed_by_name?: string | null;
  corrections_count?: number;
  /**
   * Duplicate suspicion — query-time annotation, not a stored flag.
   * > 1 means at least one other non-envelope bill in the org shares
   * this (vendor_name, invoice_number). Optional until the backend MR.
   */
  dup_count?: number;
  /**
   * How many times the backend auto-retry sweep has already replayed
   * extraction for this bill (needs_fetch / transiently-failed rows;
   * capped at 5 with exponential backoff). Lets the fail-closed review
   * rows say "retried automatically N times" instead of implying the
   * operator must retry by hand. Optional until the backend MR that
   * serializes it is deployed.
   */
  auto_retry_count?: number;
  xero_invoice_id: string;
  xero_invoice_number: string;
  xero_pushed_at: string | null;
  extracted_at: string | null;
  /**
   * Email-sourced fields are nullable: manual bills (source ===
   * 'manual') have no upstream Email so these are null on the wire.
   */
  email_id: string | null;
  email_subject: string | null;
  email_from: string | null;
  email_received_at: string | null;
  email_job_id: number | null;
  has_attachments: boolean;
  attachment_filename: string | null;
  created_at: string;
  updated_at: string;
  /**
   * Document type from AI extraction. ``statement`` / ``remittance``
   * rows that produce children become envelopes; ``tax_invoice`` is
   * the typical bill. The list endpoint hides envelopes by default —
   * this field on a list row is therefore informational, not a "is
   * this an envelope?" signal (use ``is_envelope`` for that).
   */
  document_type: BillDocumentType;
  /**
   * True for the hidden statement / remittance parent row that
   * aggregates ``child_bill_count`` children. The default list
   * endpoint filters these out — they only appear when the caller
   * passes ``?include_envelopes=true``. Kept on the contract so an
   * audit-mode UI can render them later without further changes.
   */
  is_envelope: boolean;
  /**
   * Stringified UUID of the envelope this row was exploded out of, or
   * ``null`` for top-level bills. Children sharing a parent are
   * rendered grouped in the list panel.
   */
  parent_bill_id: string | null;
  /**
   * Number of children rolled up under this row when ``is_envelope``
   * is true; ``0`` for non-envelope rows. NOT the visible-sibling
   * count after filtering — the list panel computes that on the fly.
   */
  child_bill_count: number;
}

export interface BillDetail extends BillListRow {
  line_items: BillLineItem[];
  /**
   * Canonical ``report.PurchaseOrder`` id when the allocation resolved
   * via the PO tier (PO# → PurchaseOrder → Job); null otherwise.
   * Detail-only — the list view doesn't render the PO link.
   */
  linked_po_id: number | null;
  /**
   * The typed micro-step evidence bundle the allocator emitted, for
   * auditability. ``findings`` is the ordered list of per-step results;
   * the object is intentionally open (the backend adds keys additively).
   * Detail-only — too chunky for every list row.
   */
  allocation_evidence: {
    findings?: BillAllocationFinding[];
    [key: string]: unknown;
  } | null;
  raw_extraction: Record<string, unknown>;
  /**
   * Per-field/line bounding boxes for overlaying highlight rectangles on
   * the source PDF, keyed to the active field (1d). Empty array until the
   * extractor emits coordinates — the read↔form cross-highlight (1c) is
   * the always-on fallback. See ``BillFieldRegion``.
   */
  field_regions: BillFieldRegion[];
  extraction_error: string;
  reviewed_at: string | null;
  xero_push_error: string;
  /**
   * Email-sourced detail fields, nullable for manual bills (source
   * === 'manual'). Added 2026-05-17 with the manual-bill entry path.
   */
  email_from_name: string | null;
  email_body_preview: string | null;
  attachment_id: string | null;
  attachment_size: number | null;
  attachment_content_type: string | null;
  /**
   * UUIDs of the children exploded out of this envelope, in the
   * order they appeared in the source statement. Empty array for
   * non-envelope rows. Detail-only because the list endpoint already
   * hides envelopes — no point shipping the array on every row.
   */
  child_bill_ids: string[];
  /**
   * Optional supplier-labelled "extra charge" carve-out (Freight,
   * Shipping, Handling, …). The supplier's ``subtotal`` already
   * INCLUDES this value — it exists as a separate field so the UI
   * can display it with the supplier's own wording, and so the Xero
   * push path can emit it as a discrete ``LineItem`` instead of
   * losing it inside the AI-extracted subtotal.
   *
   * Wire shape: DRF emits ``DecimalField`` as a string (``"15.00"``)
   * on read. ``null`` for bills that have no extra charge.
   *
   * NOT a participant in the local ``Subtotal + GST = Total``
   * validation — that invariant continues to hold against the
   * supplier's own totals.
   */
  extra_charge_amount: string | null;
  /**
   * Supplier's wording for the charge ('Freight', 'Shipping',
   * 'Handling Fee', …). Empty string when the bill has no extra
   * charge, OR when the AI couldn't determine the wording — the UI
   * falls back to ``'Extra charges'`` at render time. Detail-only
   * (intentionally not on ``BillListRow``: the list view doesn't
   * render this surface).
   */
  extra_charge_name: string;
  /**
   * Optional link to the canonical org Supplier (quotebuilder.Supplier —
   * the SAME table Xero contact sync, the email "Add as Supplier" action,
   * and manual entry write to). ``supplier`` is the FK id (UUID string) for
   * PATCH; ``supplier_detail`` is the read-only nested summary so the panel
   * can show the structured identity + provenance (incl. Xero link) without
   * a second request. Both null when the bill is filed on free-text
   * ``vendor_name`` alone.
   */
  supplier: string | null;
  supplier_detail: BillSupplierDetail | null;
  /**
   * Paid state of the bill. ``payment_status`` is the enum; the rest carry
   * the captured payment details when ``paid``. All mutated ONLY via the
   * ``mark-paid`` action (read-only on the generic PATCH). ``paid_amount`` is
   * DRF's stringified DecimalField (``"2780.91"``) or null when never paid.
   * ``paid_matched_bank_ref`` is reserved for the future bank-feed match —
   * empty until feeds are connected.
   */
  payment_status: BillPaymentStatus;
  paid_at: string | null;
  paid_amount: string | null;
  payment_reference: string;
  payment_method: string;
  paid_matched_bank_ref: string;
  /**
   * Due date PREDICTED from the bill's EFFECTIVE term + ``invoice_date``
   * (ISO date string), or null when no term applies / there is no invoice
   * date. Advisory only — ``due_date`` stays authoritative. When the two
   * differ, the invoice stated a date its own terms don't imply.
   */
  predicted_due_date: string | null;
  /**
   * This bill's own term — the EXCEPTION channel. ``''`` (the usual case)
   * means it inherits the supplier's; ``exempt`` means it follows none.
   * Read-only here: set it through the ``supplier-term`` action, which is
   * where the bill-vs-supplier scope is chosen.
   */
  payment_term_type: BillTermType | '';
  payment_term_days: number | null;
  /** Label of the EFFECTIVE term, whoever it came from ('' when none). */
  payment_term_label: string;
  /** Which level that effective term came from. */
  payment_term_source: BillTermSource;
}

/** Read-only summary of a bill's linked Supplier (BillDetail.supplier_detail). */
export interface BillSupplierDetail {
  id: string;
  name: string;
  code: string;
  source: string;
  email: string | null;
  /** Xero contact id when this Supplier is the Xero-synced record; else null. */
  xero_contact_id: string | null;
  /**
   * Structured internal payment term carried by the supplier (CoD/7/30/45/
   * 60/90/EOM/Custom), or null when unset. ``payment_term_days`` is the offset
   * for ``custom``; ``payment_term_label`` is the server-rendered human label
   * ("30 days", "Custom (21 days)"). ``payment_terms`` is the legacy free-text
   * field kept for display fallback.
   */
  payment_term_type: SupplierTermType | null;
  payment_term_days: number | null;
  payment_term_label: string | null;
  payment_terms: string | null;
}

/** One row in the supplier picker (GET /api/quotebuilder/suppliers/). */
export interface BillSupplierOption {
  id: string;
  name: string;
  code?: string;
  source?: string;
  email?: string | null;
  xero_contact_id?: string | null;
}

/**
 * Wire body for the inline "create supplier from this vendor" action in the
 * bill supplier-picker (POST /api/quotebuilder/suppliers/, source='manual').
 * The client mints the unique-per-org ``code`` itself (see
 * ``buildSupplierCode``) so callers only pass the human fields already on the
 * bill's vendor. ``email`` / ``abn`` are optional and only sent when present.
 */
export interface SupplierCreateInput {
  name: string;
  email?: string | null;
  abn?: string | null;
}

export interface BillsListCounts {
  total: number;
  by_status: Partial<Record<BillStatus, number>>;
  /**
   * Attention-reason counts (org-wide, envelope-excluded) so every queue
   * chip is backed by a count that reconciles with a real ``?flag=``
   * filter. ``unreadable + rejected === by_status.failed`` by
   * construction. Optional until the backend MR is deployed.
   */
  flags?: {
    unreadable: number;
    rejected: number;
    no_amount: number;
    duplicate: number;
  };
}

/**
 * Money summary over the FULL filtered set, per currency — computed
 * server-side so the queue header never presents a page-local sum as the
 * queue's money. ``known_total`` sums only non-NULL totals;
 * ``unknown_count`` is the number of bills with NO trustworthy amount (a
 * first-class figure, never folded into the sum); overdue = past-due AND
 * unpaid. Mixed-currency tenants get one row per currency — the consumer
 * must render them separately, never blend them.
 */
export interface BillsQueueCurrencySummary {
  currency: string;
  known_total: string;
  known_count: number;
  unknown_count: number;
  overdue_known_total: string;
  overdue_count: number;
}

export interface BillsQueueSummary {
  known_count: number;
  unknown_count: number;
  overdue_count: number;
  by_currency: BillsQueueCurrencySummary[];
}

/**
 * Pagination envelope for the filtered (status + q) slice, added with
 * server-side pagination (P1-1). OPTIONAL + additive — an older backend
 * that predates it simply omits ``page``; consumers must treat a missing
 * ``page`` as "single full page, no more to load" (back-compat).
 */
export interface BillsListPage {
  /** Page size that was applied (server clamps explicit paging to a max). */
  limit: number;
  /** Zero-based offset of this slice within the filtered set. */
  offset: number;
  /** Number of rows actually returned in ``bills`` for this slice. */
  returned: number;
  /** Total rows in the filtered (status + q) set across all pages. */
  total: number;
  /** True when ``offset + limit < total`` — i.e. another page exists. */
  has_more: boolean;
}

export interface BillsListResponse {
  bills: BillListRow[];
  counts: BillsListCounts;
  /** Present when the backend supports pagination (P1-1+). See BillsListPage. */
  page?: BillsListPage;
  /** Full-filtered-set money summary (queue redesign). Optional until the
   * backend MR is deployed; consumers MUST fall back to labelling any
   * page-local computation as partial — never presenting it as the total. */
  summary?: BillsQueueSummary;
}

/**
 * Wire body for ``POST /api/bills/`` (manual bill entry, added
 * 2026-05-17). The drawer-create UX in
 * ``apps/{newmark,sam}/src/features/bills/components/BillDrawer.tsx``
 * posts this when the operator clicks "+ New Bill" on a tenant
 * without Xero or without a connected mailbox.
 *
 * Backend contract: BillManualCreateSerializer in
 * organisation/views/bill_views/manual_create_serializer.py. The
 * server fills ``source='manual'``, ``status='reviewed'``,
 * ``email=null`` and returns the row as ``BillDetail``.
 *
 * Decimal-typed fields accept either ``number`` or ``string`` on
 * the wire (DRF's DecimalField coerces both). We pass strings from
 * the form so we don't accumulate floating-point error during
 * intermediate edits ("100.10" → 100.099999...).
 */
export interface BillManualCreatePayload {
  vendor_name: string;
  vendor_email?: string;
  vendor_abn?: string;
  invoice_number?: string;
  reference?: string;
  invoice_date?: string | null;
  due_date?: string | null;
  subtotal?: string | number | null;
  gst?: string | number | null;
  total: string | number;
  currency?: string;
  line_items?: Array<{
    description: string;
    quantity?: string | number | null;
    unit_amount?: string | number | null;
    line_total: string | number;
    account_code?: string;
    tax_type?: string;
    category?: string;
  }>;
  /**
   * Free-form audit note persisted to ``raw_extraction.notes`` on
   * the row. Surfaces in the drawer's "About" panel after create.
   */
  notes?: string;
}

/**
 * Which CSV shape ``GET /api/bills/export/`` should stream.
 *
 * ``'xero-bills-csv'`` — column-for-column match for Xero's Bills
 * Import template; bookkeepers drop the file into Business → Bills
 * to pay → Import. One row per line item.
 *
 * ``'reconciliation'`` — tickable worksheet (one row per bill,
 * status + Xero ID + push error) for offline reconciliation against
 * bank statements when Xero isn't connected.
 */
export type BillsExportFormat = 'xero-bills-csv' | 'reconciliation';

export interface BillStats {
  counts: {
    total: number;
    by_status: Record<BillStatus, number>;
  };
  amounts: {
    total: string;
    by_status: Record<BillStatus, string>;
    currency: string;
  };
  processing: {
    ai_extracted_pct: number;
    auto_pushed_pct: number;
    approval_pct: number;
    extracted_24h: number;
    /** Bills approved (extracted → reviewed) in the last 24h. Backs the
     *  Approvals "Approved (24h)" stat. Added 2026-06-20. */
    reviewed_24h: number;
    pushed_24h: number;
  };
  recent: BillListRow[];
}

export interface ExtractPendingResponse {
  queued: number;
  skipped_no_attachment: number;
  limit: number;
}

export interface MarkAsBillResponse {
  bill_id: string;
  status: BillStatus;
  created: boolean;
  email_id: string;
  attachment_id: string | null;
}

/**
 * Minimal recipient + attachment + email shapes used by the right-rail
 * "Email" tab. Kept independent of the main Email Hub types so the
 * @quotech/bills package stays free of cross-feature imports — the
 * adapter in each app maps its own Email shape onto these.
 */
export interface BillEmailRecipient {
  name?: string;
  email?: string;
}

export interface BillEmailAttachment {
  id?: string | number;
  filename?: string;
  content_type?: string;
  size?: number;
  is_inline?: boolean;
  /** RFC-822 Content-ID without surrounding angle brackets. Used to
   *  resolve ``<img src="cid:VALUE">`` refs in body_html. */
  content_id?: string;
  /** Signed inline-serve URL — populated for image attachments only. */
  inline_url?: string | null;
}

export interface BillEmailDetail {
  id: string;
  subject: string;
  from_address: string;
  from_name?: string;
  to_addresses?: BillEmailRecipient[];
  cc_addresses?: BillEmailRecipient[];
  body_text?: string;
  body_html?: string;
  body_preview?: string;
  received_at: string;
  attachments?: BillEmailAttachment[];
}

/**
 * Subset of BillDetail fields the UI lets users edit inline (PATCH /api/bills/{id}/).
 * Keep this in sync with `BillDetailSerializer.update_allowed_fields` on the backend
 * to prevent silent drops on save.
 */
export type BillEditablePatch = Partial<
  Pick<
    BillDetail,
    | 'vendor_name'
    | 'vendor_email'
    | 'vendor_abn'
    | 'invoice_number'
    | 'reference'
    | 'invoice_date'
    | 'due_date'
    | 'subtotal'
    | 'gst'
    | 'total'
    | 'currency'
    | 'line_items'
    | 'extra_charge_amount'
    | 'extra_charge_name'
    | 'supplier'
  >
>;

/* ════════════════════════════════════════════════════════════════════
 * Supplier payment terms + bill paid state + bank-feed match
 *
 * Backend contract (2026-06-18):
 *   organisation/views/bill_views/payment_views.py
 *     POST /api/bills/<id>/supplier-term/      — set supplier term, re-derive
 *     POST /api/bills/<id>/mark-paid/          — set / clear paid state
 *     GET  /api/bills/<id>/payment-suggestions/ — bank-feed match (stub)
 * All three return / consume the shapes below.
 * ════════════════════════════════════════════════════════════════════ */

/**
 * Body for ``POST /api/bills/<id>/supplier-term/``.
 *
 * ``scope`` decides who the term is written to, and the blast radius
 * differs sharply:
 *   ``'supplier'`` (the default) — writes the vendor's term, so every bill
 *      from them inherits it, and re-dates ALL their unpaid bills.
 *   ``'bill'`` — a one-off exception on this bill only. Only this scope
 *      accepts ``'exempt'`` ("this bill follows no term").
 *
 * ``custom_days`` is required when ``term_type === 'custom'`` and ignored
 * otherwise. ``term_type: ''`` clears the term at the chosen scope; at bill
 * scope that means "go back to inheriting the supplier".
 */
export interface SetSupplierTermInput {
  term_type: BillTermType | '';
  custom_days?: number | null;
  scope?: 'bill' | 'supplier';
}

/**
 * Body for ``POST /api/bills/<id>/mark-paid/``.
 *   To mark paid:  ``{ paid: true, paid_at?, amount?, reference?, method? }``
 *     — ``paid_at`` defaults to today, ``amount`` defaults to the bill total.
 *   To unmark:     ``{ paid: false }`` — clears all captured payment details.
 */
export interface MarkBillPaidInput {
  paid: boolean;
  paid_at?: string | null;
  amount?: string | number | null;
  reference?: string;
  method?: string;
  /** Proceed despite a 409 possible_duplicate (operator confirmed). */
  force?: boolean;
}

/**
 * Options for the money actions (approve / push-to-xero). ``force``
 * re-submits past the backend's 409 ``possible_duplicate`` guard after
 * the operator confirmed the duplicate dialog.
 */
export interface BillActionOptions {
  force?: boolean;
}

/**
 * The ``duplicate`` payload of a 409 ``code=possible_duplicate``
 * response from approve / mark-paid / push-to-xero — the other bill
 * the backend's normalised matcher believes is the same payable.
 * Rendered in the confirm dialog so the operator decides.
 */
export interface BillDuplicateInfo {
  id: string;
  invoice_number: string;
  vendor_name: string;
  total: string | null;
  status: string;
  payment_status: string;
  paid_at: string | null;
}

/**
 * One bank-feed transaction candidate that could clear this bill. The shape is
 * the forward-compatible contract the UI binds to NOW — the endpoint returns an
 * empty list until a bank feed is connected, at which point the backend
 * populates these rows (id / date / amount / description / reference / score).
 */
export interface BillPaymentSuggestion {
  id: string;
  date: string | null;
  amount: string;
  description: string;
  reference: string;
  /** 0.000–1.000 match confidence once feeds are wired. */
  score: number | null;
}

export interface BillPaymentSuggestionsResponse {
  suggestions: BillPaymentSuggestion[];
  /** False until a bank feed is connected — drives the empty-state copy. */
  bank_feed_connected: boolean;
  message: string;
}

/* ════════════════════════════════════════════════════════════════════
 * Purchase orders + bill ↔ PO allocation
 *
 * Mirrors the backend contract added 2026-05-05:
 *   organisation/views/po_views/_serializers.py
 *
 * The right-rail PO panel renders:
 *   - allocations    (existing bill ↔ PO links + per-line variance)
 *   - available POs  (open / actionable POs on the bill's linked job)
 *   - suggestions    (auto-match candidates ranked by ref/total/vendor)
 *
 * Decimal-typed fields are stringified on the wire so JSON round-tripping
 * doesn't drop precision (DRF would otherwise emit them as floats).
 * ════════════════════════════════════════════════════════════════════ */

export type PurchaseOrderStatus =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'AUTHORISED'
  | 'BILLED'
  | 'DELETED';

export type PurchaseOrderSource =
  | 'xero_sync'
  | 'manual'
  | 'email_extraction';

export interface PurchaseOrderLine {
  description?: string;
  quantity?: string | number | null;
  unit_amount?: string | number | null;
  line_amount?: string | number | null;
  item_code?: string;
  account_code?: string;
  tax_type?: string;
  [key: string]: unknown;
}

/**
 * Payment status of a PO. Computed by the backend from
 * ``XeroPurchaseOrder.amount_paid`` vs ``total`` on every payment
 * write — see ``XeroPurchaseOrder.recompute_payment_state``.
 */
export type PoPaymentStatus =
  | 'unpaid'
  | 'deposit_paid'
  | 'partially_paid'
  | 'paid'
  | 'overpaid';

export interface PurchaseOrder {
  id: number;
  purchase_order_number: string | null;
  reference: string | null;
  status: PurchaseOrderStatus;
  source: PurchaseOrderSource;
  contact_id: string | null;
  contact_name: string | null;
  sub_total: string;
  total_tax: string;
  total: string;
  currency_code: string;
  date: string | null;
  delivery_date: string | null;
  expected_arrival_date: string | null;
  job_id: number | null;
  job_number: string | null;
  xero_purchase_order_id: string;
  /** How many bill allocations reference this PO. */
  allocation_count: number;
  /** Sum of allocated_amount across all bill allocations on this PO. */
  allocated_total: string;
  /**
   * Line items inlined on the list payload too — slightly chunkier
   * than a typical "list" serializer but the right-rail panel needs
   * them for the auto-pair routine without an extra detail fetch
   * per Allocate click.
   */
  line_items: PurchaseOrderLine[];
  /**
   * Payment side (PR-2). ``amount_paid`` is the sum of PoPayment.amount
   * for this PO; ``payment_status`` is computed from it vs ``total``
   * with cents-level tolerance.
   */
  amount_paid: string;
  payment_status: PoPaymentStatus;
  /**
   * Audit-trail FKs populated when the PO was created via
   * "Convert email to PO" — null for Xero-synced + manually-entered.
   */
  source_email: string | null;
  source_attachment: string | null;
  last_synced: string;
  created_at: string;
}

export interface PurchaseOrderDetail extends PurchaseOrder {
  delivery_address: string | null;
  sent_to_contact: boolean;
}

/**
 * Per-line correspondence the bookkeeper sets when allocating a PO
 * to a bill. The backend stores these on
 * ``BillPurchaseOrderAllocation.line_matches`` and uses them to
 * compute variance.
 *
 * ``qty_billed`` / ``unit_price_billed`` snapshot the bill values at
 * allocation time so historical variance survives a later edit to
 * either side.
 */
export interface BillPoLineMatch {
  bill_line_idx: number;
  po_line_idx: number;
  qty_billed?: string | null;
  unit_price_billed?: string | null;
}

export interface BillPoAllocation {
  id: string;
  purchase_order: PurchaseOrder;
  allocated_amount: string;
  line_matches: BillPoLineMatch[];
  notes: string;
  created_at: string;
  updated_at: string;
  created_by_name: string | null;
}

export type BillPoVarianceFlag = 'ok' | 'qty_mismatch' | 'price_drift';

/**
 * One row in the right-rail variance grid — built by the backend from
 * an allocation's ``line_matches`` against the bill + PO line snapshots.
 */
export interface BillPoVarianceRow {
  allocation_id: string;
  po_id: number;
  po_number: string;
  bill_line_idx: number;
  po_line_idx: number;
  bill_description: string;
  po_description: string;
  qty_bill: string | null;
  qty_po: string | null;
  qty_diff: string | null;
  unit_price_bill: string | null;
  unit_price_po: string | null;
  unit_price_diff: string | null;
  amount_bill: string | null;
  amount_po: string | null;
  amount_diff: string | null;
  flag: BillPoVarianceFlag;
}

export interface BillPoTotals {
  currency: string;
  bill_total: string;
  allocated_total: string;
  unallocated_remainder: string;
  allocation_count: number;
}

export interface BillPoAllocationsResponse {
  allocations: BillPoAllocation[];
  totals: BillPoTotals;
  variance: BillPoVarianceRow[];
  /**
   * Echo of the bill's line items so the right-rail can render the
   * matching grid without a second fetch. Snapshot at the time of the
   * GET; the panel re-fetches on every selection change.
   */
  bill_line_items: BillLineItem[];
}

export interface PurchaseOrdersListResponse {
  purchase_orders: PurchaseOrder[];
  count: number;
}

export interface BillPoSuggestionsResponse {
  suggestions: PurchaseOrder[];
  job_id: number | null;
  bill_total: string;
  bill_reference: string;
}

/**
 * Payload accepted by ``POST /api/bills/<id>/po-allocations/`` (idempotent
 * on (bill, purchase_order) — re-posting the same pair updates rather
 * than duplicates).
 */
export interface CreateBillPoAllocationInput {
  purchase_order_id: number;
  allocated_amount: string;
  line_matches?: BillPoLineMatch[];
  notes?: string;
}

/**
 * Payload accepted by ``POST /api/purchase-orders/`` for a manual PO.
 * ``job_id`` is sent at the top level (the serializer's nested ``job``
 * shape is read-only).
 */
export interface CreatePurchaseOrderInput {
  job_id?: number | null;
  purchase_order_number?: string | null;
  reference?: string | null;
  status?: PurchaseOrderStatus;
  contact_id?: string | null;
  contact_name?: string | null;
  sub_total?: string;
  total_tax?: string;
  total?: string;
  currency_code?: string;
  date?: string | null;
  delivery_date?: string | null;
  expected_arrival_date?: string | null;
  delivery_address?: string | null;
  line_items?: PurchaseOrderLine[];
}

export type UpdatePurchaseOrderInput = Partial<
  CreatePurchaseOrderInput & { sent_to_contact?: boolean }
>;

/* ════════════════════════════════════════════════════════════════════
 * PO payments — record COD / deposit / progress payments BEFORE the
 * supplier's bill arrives. PR-2 (2026-05-05).
 *
 * Backend contract: organisation/views/po_views/payment_views.py
 * Drives the new "Payments" section on the right-rail PO panel.
 * ════════════════════════════════════════════════════════════════════ */

export type PaymentMethod =
  | 'eft'
  | 'cheque'
  | 'card'
  | 'bpay'
  | 'cash'
  | 'other';

/**
 * Inlined summary of a Xero payment when one has been linked to a
 * PoPayment. Populated server-side via the
 * ``xero_payment_summary`` SerializerMethodField so the panel can
 * render the badge without a second fetch.
 */
export interface XeroPaymentSummary {
  id: number;
  amount: string;
  date: string | null;
  reference: string;
  invoice_number: string;
  bank_account_name: string;
}

export interface SourceEmailSummary {
  id: string;
  subject: string;
  from_address: string;
  received_at: string | null;
}

export interface PoPayment {
  id: string;
  amount: string;
  paid_at: string;
  method: PaymentMethod;
  reference: string;
  notes: string;
  /** Xero payment id when the bookkeeper has linked a match; else null. */
  xero_payment: number | null;
  xero_payment_summary: XeroPaymentSummary | null;
  /** Email id when the payment was triggered by a remittance / request email. */
  source_email: string | null;
  source_email_summary: SourceEmailSummary | null;
  created_at: string;
  updated_at: string;
  created_by_name: string | null;
}

export interface CreatePoPaymentInput {
  amount: string;
  paid_at: string;
  method?: PaymentMethod;
  reference?: string;
  notes?: string;
  xero_payment?: number | null;
  source_email?: string | null;
}

/**
 * Response of ``GET /api/purchase-orders/<id>/payments/`` — payments +
 * the PO's running balance so the panel doesn't need to refetch the
 * PO detail to update the badge.
 */
export interface PoPaymentsResponse {
  payments: PoPayment[];
  amount_paid: string;
  payment_status: PoPaymentStatus;
  currency: string;
  po_total: string;
}

/** XeroPayment candidate for the "match payment" picker. */
export interface XeroPaymentSuggestion {
  id: number;
  date: string | null;
  amount: string;
  currency_code: string;
  reference: string | null;
  invoice_number: string | null;
  payment_type: string;
  bank_account_name: string | null;
}

export interface XeroPaymentSuggestionsResponse {
  suggestions: XeroPaymentSuggestion[];
  po_total: string;
  po_remaining: string;
  currency: string;
}

/* ════════════════════════════════════════════════════════════════════
 * Convert-email-to-PO + payment-requests inbox lane (PR-2)
 *
 * Backend contract: organisation/views/po_views/{convert_email_views,
 * payment_request_views}.py
 * ════════════════════════════════════════════════════════════════════ */

export interface ConvertEmailToPoInput {
  job_id?: number | null;
  purchase_order_number?: string | null;
  reference?: string | null;
  supplier_name?: string | null;
  contact_id?: string | null;
  total?: string | null;
  sub_total?: string | null;
  total_tax?: string | null;
  currency_code?: string;
  date?: string | null;
  delivery_address?: string | null;
  line_items?: PurchaseOrderLine[];
  attachment_id?: string | null;
  status?: PurchaseOrderStatus;
}

export interface PaymentRequestEmailRow {
  email_id: string;
  subject: string;
  from_address: string;
  from_name: string;
  received_at: string | null;
  category: string;
  job_id: number | null;
  attachment: {
    id: string;
    filename: string;
    size: number | null;
    content_type: string;
  } | null;
  /** Set when "Convert to PO" has already been run on this email. */
  spawned_po: {
    id: number;
    purchase_order_number: string;
    amount_paid: string;
    payment_status: PoPaymentStatus;
    total: string;
  } | null;
}

export interface PaymentRequestsResponse {
  emails: PaymentRequestEmailRow[];
  count: number;
}
